/**
 * ServicesGrid — Shows all 4 trades as premium service cards.
 * Clicking a trade card switches the active trade and scrolls to the quote builder.
 */

import COLORS from "../config/colors";

const C = COLORS;

// SVG icons per trade
function DeckIcon({ color }) {
  return (
    <svg width="36" height="36" viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="28" width="40" height="8" rx="2" fill={color} opacity="0.15"/>
      <rect x="4" y="28" width="40" height="8" rx="2"/>
      <line x1="12" y1="28" x2="12" y2="44"/>
      <line x1="24" y1="28" x2="24" y2="44"/>
      <line x1="36" y1="28" x2="36" y2="44"/>
      <path d="M8 28V20M40 28V20"/>
      <path d="M4 20h40"/>
      <path d="M12 20V14M36 20V14"/>
      <path d="M8 14h32"/>
    </svg>
  );
}

function ConcreteIcon({ color }) {
  return (
    <svg width="36" height="36" viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="30" width="40" height="10" rx="2" fill={color} opacity="0.15"/>
      <rect x="4" y="30" width="40" height="10" rx="2"/>
      <path d="M4 30l6-16h28l6 16"/>
      <line x1="14" y1="30" x2="10" y2="14"/>
      <line x1="24" y1="30" x2="24" y2="14"/>
      <line x1="34" y1="30" x2="38" y2="14"/>
      <line x1="4" y1="22" x2="44" y2="22"/>
    </svg>
  );
}

function PaintIcon({ color }) {
  return (
    <svg width="36" height="36" viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 38c0 2.2 1.8 4 4 4s4-1.8 4-4c0-3-4-8-4-8s-4 5-4 8z" fill={color} opacity="0.25"/>
      <path d="M10 38c0 2.2 1.8 4 4 4s4-1.8 4-4c0-3-4-8-4-8s-4 5-4 8z"/>
      <path d="M14 30V12"/>
      <path d="M10 12h18a4 4 0 010 8H10V12z" fill={color} opacity="0.15"/>
      <path d="M10 12h18a4 4 0 010 8H10V12z"/>
      <path d="M32 16h8v8h-8"/>
    </svg>
  );
}

function RoofIcon({ color }) {
  return (
    <svg width="36" height="36" viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="24,6 4,26 8,26 8,42 40,42 40,26 44,26" fill={color} opacity="0.12"/>
      <polygon points="24,6 4,26 8,26 8,42 40,42 40,26 44,26"/>
      <line x1="16" y1="42" x2="16" y2="30"/>
      <line x1="32" y1="42" x2="32" y2="30"/>
      <rect x="16" y="30" width="16" height="12"/>
      <line x1="14" y1="34" x2="34" y2="34"/>
      <line x1="14" y1="30" x2="34" y2="30"/>
      <line x1="11" y1="26" x2="37" y2="26"/>
    </svg>
  );
}

const TRADE_CONFIG = {
  decks: {
    icon: (color) => <DeckIcon color={color} />,
    accentColor: C.pine,
    title: "Custom Decks",
    subtitle: "Composite · Hardwood · Pressure-Treated",
    description:
      "Full outdoor living builds with railings, pergolas, lighting, and fire pit pads. From Trex Enhance to Ipê hardwood — we spec the right material for your climate.",
    bullets: ["Composite & wood options", "Railings & pergolas", "Built to local snow loads"],
    startingAt: "From $14/sqft installed",
  },
  concrete: {
    icon: (color) => <ConcreteIcon color={color} />,
    accentColor: C.slate,
    title: "Concrete Flatwork",
    subtitle: "Driveways · Patios · Pool Decks",
    description:
      "Broom, stamped, exposed aggregate, and polished finishes. Every pour is engineered for your soil type, frost depth, and freeze-thaw cycle.",
    bullets: ["Freeze-thaw rated mixes", "5 finish options", "Drainage & base engineering"],
    startingAt: "From $9/sqft installed",
  },
  painting: {
    icon: (color) => <PaintIcon color={color} />,
    accentColor: C.blue,
    title: "Interior & Exterior Painting",
    subtitle: "Full Repaints · Cabinets · Accents",
    description:
      "Professional crews using Sherwin-Williams and Benjamin Moore. Full prep, prime, and paint — exterior siding, trim, windows, interior rooms, and cabinet refinishing.",
    bullets: ["SW & BM premium materials", "Exterior & interior", "Power wash & caulk prep"],
    startingAt: "From $1.75/sqft installed",
  },
  roofing: {
    icon: (color) => <RoofIcon color={color} />,
    accentColor: C.red,
    title: "Roofing",
    subtitle: "Shingles · Metal · Membrane",
    description:
      "Architectural shingles, Class 4 impact-resistant, standing seam metal, and low-slope TPO/membrane systems. Permits pulled. Fixed-bid scope in writing. Built for your wind and hail zone.",
    bullets: ["Class 4 impact-resistant options", "Metal & membrane systems", "Permits pulled · Fixed-bid scope in writing"],
    startingAt: "From $6.50/sqft installed",
  },
};

function TradeCard({ tradeSlug, tradeConfig, isActive, onClick }) {
  const cfg = tradeConfig;
  const accent = cfg.accentColor;

  return (
    <button
      onClick={() => onClick(tradeSlug)}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        padding: "28px 28px 24px",
        borderRadius: 18,
        border: `2px solid ${isActive ? `${accent}40` : "rgba(155,142,130,0.10)"}`,
        background: isActive ? C.white : "rgba(44,24,16,0.04)",
        cursor: "pointer",
        fontFamily: "'DM Sans', sans-serif",
        boxShadow: isActive
          ? "0 4px 24px rgba(44,24,16,0.12)"
          : "none",
        opacity: isActive ? 1 : 0.65,
        transition: "all 0.2s ease",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={e => {
        if (!isActive) {
          e.currentTarget.style.boxShadow = `0 8px 32px ${accent}20`;
          e.currentTarget.style.borderColor = `${accent}60`;
          e.currentTarget.style.transform = "translateY(-2px)";
        }
      }}
      onMouseLeave={e => {
        if (!isActive) {
          e.currentTarget.style.boxShadow = "0 1px 6px rgba(44,24,16,0.06)";
          e.currentTarget.style.borderColor = "rgba(155,142,130,0.15)";
          e.currentTarget.style.transform = "";
        }
      }}
    >
      {/* Active indicator */}
      {isActive && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: accent,
            borderRadius: "18px 18px 0 0",
          }}
        />
      )}

      {/* Icon */}
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 14,
          background: `${accent}14`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 18,
          flexShrink: 0,
        }}
      >
        {cfg.icon(accent)}
      </div>

      {/* Title + subtitle */}
      <div style={{ fontWeight: 700, fontSize: 18, color: C.bark, marginBottom: 4 }}>
        {cfg.title}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: accent, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 14 }}>
        {cfg.subtitle}
      </div>

      {/* Description */}
      <p style={{ fontSize: 14, color: C.stone, lineHeight: 1.65, marginBottom: 16, flex: 1 }}>
        {cfg.description}
      </p>

      {/* Bullets */}
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px", display: "flex", flexDirection: "column", gap: 6 }}>
        {cfg.bullets.map(b => (
          <li key={b} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.bark, fontWeight: 500 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: accent, flexShrink: 0 }} />
            {b}
          </li>
        ))}
      </ul>

      {/* Starting price + CTA */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.stone }}>{cfg.startingAt}</span>
        <span
          style={{
            padding: "8px 18px",
            borderRadius: 8,
            background: isActive ? "transparent" : accent,
            border: `2px solid ${accent}`,
            color: isActive ? accent : C.white,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.3,
          }}
        >
          {isActive ? "Selected ✓" : "Get Quote →"}
        </span>
      </div>
    </button>
  );
}

export default function ServicesGrid({ market, activeTrade, onTradeSelect }) {
  const availableTrades = market?.trades || Object.keys(TRADE_CONFIG);

  return (
    <section style={{ padding: "64px 0 48px", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @media (max-width: 640px) {
          .services-section { padding: 40px 0 32px !important; }
        }
      `}</style>
      {/* Section header */}
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 3, color: C.honey, marginBottom: 12 }}>
          What We Do
        </div>
        <h2
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(28px, 4vw, 44px)",
            fontWeight: 800,
            color: C.bark,
            margin: "0 0 16px",
            lineHeight: 1.15,
          }}
        >
          Four Trades.
          <span style={{ color: C.honey }}> One Crew. </span>
          Done Right.
        </h2>
        <p style={{ fontSize: 16, color: C.stone, maxWidth: 560, margin: "0 auto", lineHeight: 1.65 }}>
          {market
            ? `Serving ${market.displayName} and the surrounding area with fixed-bid, permit-pulled, locally-operated craftsmanship.`
            : "Fixed-bid, permit-pulled, locally-operated craftsmanship across the US."}
        </p>
      </div>

      {/* Cards grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 20,
        }}
      >
        {availableTrades
          .filter(t => TRADE_CONFIG[t])
          .map(tradeSlug => (
            <TradeCard
              key={tradeSlug}
              tradeSlug={tradeSlug}
              tradeConfig={TRADE_CONFIG[tradeSlug]}
              isActive={activeTrade?.slug === tradeSlug}
              onClick={onTradeSelect}
            />
          ))}
      </div>

      {/* Differentiator strip */}
      <div
        style={{
          marginTop: 40,
          padding: "24px 32px",
          borderRadius: 16,
          background: C.bark,
          color: C.white,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 20,
          alignItems: "center",
          justifyItems: "center",
        }}
      >
        {[
          { stat: "Fixed-Bid",    label: "No cost overruns — ever" },
          { stat: "20–30%",       label: "Above market quality, priced to match" },
          { stat: "Permitted",    label: "Every job pulled and inspected" },
          { stat: "28 Markets",   label: "Across the country" },
        ].map(({ stat, label }) => (
          <div key={stat} style={{ textAlign: "center", minWidth: 120 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.honey, fontFamily: "'Playfair Display', serif" }}>
              {stat}
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", marginTop: 4 }}>{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
